import type { SVGProps } from 'react';
import { interpolate, spring, useCurrentFrame, useVideoConfig } from 'remotion';

// Types
import type { WindLine } from './types';

// Constants
import {
  ANIMATION_CONFIG,
  SMIL_ANIMATION,
  SPRING_CONFIG,
  STAR_PATH_DATA,
  WIND_LINES,
} from './constants';

// Utils
import { getBounceY, getDashArray, getDashOffset, getZoomScale } from './utils';

/**
 * Individual wind line with SMIL animations
 */
const WindLineElement = ({ line }: { line: WindLine }) => (
  <path
    d={line.d}
    strokeDasharray={`0 ${line.dashArray}`}
    strokeDashoffset="0"
    opacity={line.opacity}
  >
    {/* Dash array creates drawing effect */}
    <animate
      attributeName="stroke-dasharray"
      begin={line.begin}
      dur={line.duration}
      repeatCount={SMIL_ANIMATION.REPEAT_COUNT}
      values={getDashArray(line)}
      keyTimes={SMIL_ANIMATION.KEY_TIMES}
    />
    {/* Dash offset creates movement effect */}
    <animate
      attributeName="stroke-dashoffset"
      begin={line.begin}
      dur={line.duration}
      repeatCount={SMIL_ANIMATION.REPEAT_COUNT}
      values={getDashOffset(line)}
      keyTimes={SMIL_ANIMATION.KEY_TIMES}
    />
  </path>
);

/**
 * Wind lines group with varying opacity
 */
const WindLinesGroup = () => (
  <g
    id="wind"
    className="stroke-current stroke-[4] stroke-linecap-round fill-none"
    style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
  >
    {WIND_LINES.map((line, index) => (
      <WindLineElement key={index} line={line} />
    ))}
  </g>
);

/**
 * Static star shape
 */
const StarShape = () => (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d={STAR_PATH_DATA}
    className="fill-current opacity-100"
    style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
  />
);

/**
 * Animated star with bouncing and zooming
 */
export const StarAnimation = ({ ...props }: SVGProps<SVGSVGElement>) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Spring progress (0 to 1) for fly-in
  const arrivalProgress = spring({ frame, fps, config: SPRING_CONFIG });

  // Horizontal position: fly from Polaroid edge
  const horizontalOffset = interpolate(
    arrivalProgress,
    [0, 1],
    [ANIMATION_CONFIG.HORIZONTAL_START_OFFSET, 0],
  );

  // Scale: small → overshoot → settle
  const flyInScale = interpolate(
    arrivalProgress,
    ANIMATION_CONFIG.FLY_IN_SCALE.keyframes,
    ANIMATION_CONFIG.FLY_IN_SCALE.values,
  );

  // Opacity: fade in
  const opacity = interpolate(
    arrivalProgress,
    ANIMATION_CONFIG.FLY_IN_OPACITY.keyframes,
    ANIMATION_CONFIG.FLY_IN_OPACITY.values,
  );

  // Vertical bounce: continuous after arrival
  const verticalBounceOffset = getBounceY(frame, arrivalProgress);

  // Scale zoom: breathing effect after arrival
  const scaleMultiplier = getZoomScale(frame, arrivalProgress);

  return (
    <svg
      data-testid="star-animation"
      viewBox="0 0 65 53"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="size-16 text-gold-200 overflow-visible"
      style={{
        transform: `translate(${horizontalOffset}px, ${verticalBounceOffset}px) scale(${flyInScale * scaleMultiplier})`,
        transformOrigin: 'center center',
        opacity,
      }}
      {...props}
    >
      <WindLinesGroup />
      <StarShape />
    </svg>
  );
};
